import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition, Listbox } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// Strip HTML tags from text
function stripHtml(html: string): string {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface Case {
  id: string;
  number: string;
  shortDescription: string;
  ticketType: string;
  state: string;
  priority: string;
  Customer?: {
    accountName: string;
  };
  Engagement?: {
    name: string;
  };
}

interface AddRelatedRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  caseNumber: string;
  ticketType: string;
  onSuccess?: () => void; // Optional callback when related record is added
}

const RELATIONSHIP_TYPES = [
  { value: 'RELATED_TO', label: 'Related', description: 'Independent relationship, no cascading effects' },
  { value: 'CROSS_ENTITY', label: 'Cross-Entity', description: 'Link to different entity types' }
];

const ENTITY_TYPES = [
  { value: 'INCIDENT', label: 'Incident' },
  { value: 'PROBLEM', label: 'Problem' },
  { value: 'CHANGE', label: 'Change Request' },
  { value: 'SERVICE_REQUEST', label: 'Service Request' }
];

const AddRelatedRecordModal: React.FC<AddRelatedRecordModalProps> = ({
  isOpen,
  onClose,
  caseId,
  caseNumber,
  ticketType: _ticketType,
  onSuccess
}) => {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntityType, setSelectedEntityType] = useState(ENTITY_TYPES[0]);
  const [selectedRelationshipType, setSelectedRelationshipType] = useState(RELATIONSHIP_TYPES[0]);
  const [searchResults, setSearchResults] = useState<Case[]>([]);
  const [selectedCases, setSelectedCases] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Debounced search query for autocomplete
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSearchResults([]);
      setSelectedCases([]);
      setErrorMessage(null);
      setSelectedEntityType(ENTITY_TYPES[0]);
      setSelectedRelationshipType(RELATIONSHIP_TYPES[0]);
    }
  }, [isOpen]);

  // Auto-search when debounced query or entity type changes
  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchQuery.trim() || debouncedSearchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      setErrorMessage(null);

      try {
        // All ticket types use the /cases endpoint with ticketType filter
        const response = await api.get(`/cases`, {
          params: {
            search: debouncedSearchQuery,
            ticketType: selectedEntityType.value,
            limit: 20
          }
        });

        // Filter out the current case
        const results = (response.data.cases || []).filter(
          (c: Case) => c.id !== caseId
        );

        setSearchResults(results);
      } catch (error) {
        console.error('Search failed:', error);
        setErrorMessage('Failed to search records');
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [debouncedSearchQuery, selectedEntityType, caseId]);

  // Add related records mutation
  const addMutation = useMutation({
    mutationFn: async () => {
      const relationships = selectedCases.map(targetId => ({
        targetId,
        relationshipType: selectedRelationshipType.value
      }));

      const response = await api.post(`/cases/${caseId}/related-records`, {
        relationships
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['related-records', caseId] });
      queryClient.invalidateQueries({ queryKey: ['case-activities', caseId] });
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.message || 'Failed to add related records';
      const errors = error.response?.data?.errors;
      if (errors && errors.length > 0) {
        setErrorMessage(`${errorMsg}\n${errors.join('\n')}`);
      } else {
        setErrorMessage(errorMsg);
      }
    }
  });

  const handleToggleCase = (caseId: string) => {
    setSelectedCases(prev => {
      if (prev.includes(caseId)) {
        return prev.filter(id => id !== caseId);
      } else {
        return [...prev, caseId];
      }
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedCases.length === 0) {
      setErrorMessage('Please select at least one record to relate');
      return;
    }
    addMutation.mutate();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    Add Related Record to {caseNumber}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                  {/* Relationship Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relationship Type
                    </label>
                    <Listbox value={selectedRelationshipType} onChange={setSelectedRelationshipType}>
                      <div className="relative mt-1">
                        <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm">
                          <span className="block truncate">{selectedRelationshipType.label}</span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </span>
                        </Listbox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {RELATIONSHIP_TYPES.map((type) => (
                              <Listbox.Option
                                key={type.value}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
                                  }`
                                }
                                value={type}
                              >
                                {({ selected }) => (
                                  <>
                                    <div>
                                      <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                        {type.label}
                                      </span>
                                      <span className="block text-xs text-gray-500">{type.description}</span>
                                    </div>
                                    {selected && (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>

                  {/* Entity Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Entity Type
                    </label>
                    <Listbox value={selectedEntityType} onChange={setSelectedEntityType}>
                      <div className="relative mt-1">
                        <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent sm:text-sm">
                          <span className="block truncate">{selectedEntityType.label}</span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                          </span>
                        </Listbox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {ENTITY_TYPES.map((type) => (
                              <Listbox.Option
                                key={type.value}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                    active ? 'bg-indigo-100 text-indigo-900' : 'text-gray-900'
                                  }`
                                }
                                value={type}
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {type.label}
                                    </span>
                                    {selected && (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  </div>

                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Search Records
                    </label>
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Type at least 2 characters to search..."
                        className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      />
                      {isSearching && (
                        <div className="absolute right-3 top-2.5">
                          <svg className="animate-spin h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </div>
                      )}
                    </div>
                    {searchQuery && searchQuery.length < 2 && (
                      <p className="mt-1 text-xs text-gray-500">Type at least 2 characters to search</p>
                    )}
                  </div>

                  {/* Error Message */}
                  {errorMessage && (
                    <div className="rounded-md bg-red-50 p-4">
                      <div className="text-sm text-red-700 whitespace-pre-line">{errorMessage}</div>
                    </div>
                  )}

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
                      <div className="bg-gray-50 px-4 py-2 border-b border-gray-300">
                        <span className="text-sm font-medium text-gray-700">
                          {selectedCases.length} of {searchResults.length} selected
                        </span>
                      </div>
                      <ul className="divide-y divide-gray-200">
                        {searchResults.map((result) => (
                          <li
                            key={result.id}
                            onClick={() => handleToggleCase(result.id)}
                            className={`px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                              selectedCases.includes(result.id) ? 'bg-indigo-50' : ''
                            }`}
                          >
                            <div className="flex items-start">
                              <div className="flex items-center h-5">
                                <input
                                  type="checkbox"
                                  checked={selectedCases.includes(result.id)}
                                  onChange={() => {}}
                                  className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                              </div>
                              <div className="ml-3 flex-1">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-indigo-600">{result.number}</span>
                                  <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-800">
                                    {result.ticketType.replace('_', ' ')}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-900 mt-1 line-clamp-2">{stripHtml(result.shortDescription)}</p>
                                <div className="mt-1 text-xs text-gray-500 space-x-4">
                                  <span>State: {result.state}</span>
                                  <span>Priority: {result.priority}</span>
                                  {result.Customer && <span>Customer: {result.Customer.accountName}</span>}
                                </div>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* No Results */}
                  {searchQuery && searchResults.length === 0 && !isSearching && (
                    <div className="text-center py-8 text-gray-500">
                      No records found matching your search.
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={selectedCases.length === 0 || addMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400"
                    >
                      {addMutation.isPending ? 'Adding...' : `Add ${selectedCases.length} Related Record(s)`}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddRelatedRecordModal;
